import { useEffect, useState } from 'react';
import { Lock } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DgbLogoPrimary } from '@/components/brand-logo';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PrivacyNoticeSummary } from '@/consent/PrivacyNoticeSummary';
import { clearSessionEndedNotice, readSessionEndedNotice } from '@/lib/session-ended-notice';
import { usePendingWrite } from '@/lib/use-pending-write';
import { useAuth } from './AuthProvider';

interface EmailLinkFormValues {
  email: string;
}

/** The visitor closed the Google window, or opened a second one over it. */
function isCancelledPopup(cause: unknown): boolean {
  const code =
    typeof cause === 'object' && cause !== null && 'code' in cause ? cause.code : undefined;
  return code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request';
}

/**
 * Signed-out entry point. Two sign-in paths (SL-0045 Activity 3): the Google Workspace control
 * stays PRIMARY AND FIRST — untouched behaviour — with the email-link block beneath it for
 * external users, its states driven by `emailLinkOutcome`. In the `'email-required'` cross-device
 * return leg this page is reached THROUGH `AuthGate`'s completing branch (the one sub-state that
 * needs user input), and the form's submit COMPLETES the sign-in rather than sending a new link —
 * that dispatch lives in `requestEmailLink` itself (see `AuthProvider`).
 */
export function LoginPage() {
  const { signInWithGoogleWorkspace, requestEmailLink, resetEmailLinkRequest, emailLinkOutcome } =
    useAuth();
  const [error, setError] = useState<string | null>(null);
  const [emailLinkError, setEmailLinkError] = useState<string | null>(null);
  // Shown once: read at the first render, cleared after it, so a reload no longer shows it.
  const [sessionEnded] = useState(readSessionEndedNotice);
  useEffect(clearSessionEndedNotice, []);
  const { t } = useTranslation(['auth', 'shell', 'errors']);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailLinkFormValues>({ defaultValues: { email: '' } });
  // The in-flight guard from the outset (SL-0045 Task 3.3): `handleSubmit` at the pinned
  // react-hook-form 7.80.0 has no re-entrancy early-return, so only the hook's synchronously
  // checked ref closes the window before React commits the disabled attribute.
  const { pending, run } = usePendingWrite();

  const handleSignIn = () => {
    setError(null);
    signInWithGoogleWorkspace().catch((cause: unknown) => {
      // Never `cause.message`, as on the email-link path below: Firebase's strings are untranslated
      // developer text («Firebase: Error (auth/…)») shown to someone not yet signed in. Closing the
      // Google window is the visitor's own choice, not a failure, so it says nothing.
      if (isCancelledPopup(cause)) {
        return;
      }
      setError(t('errors:signInFailed'));
    });
  };

  const onEmailLinkSubmit = async (values: EmailLinkFormValues) => {
    setEmailLinkError(null);
    try {
      await requestEmailLink(values.email);
    } catch {
      // Deliberately NOT `cause.message`: Firebase's own error strings are untranslated developer
      // text, and on this path they name configuration (`auth/unauthorized-continue-uri`,
      // `auth/quota-exceeded`) to an unauthenticated visitor. The console records the detail;
      // the page states the outcome.
      setEmailLinkError(t('auth:emailLink.requestFailed'));
    }
  };

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-[1320px] items-center justify-center p-[clamp(16px,2.4vw,28px)]">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-foreground flex justify-center">
          <DgbLogoPrimary className="h-12" />
        </div>
        <Card className="w-full">
          <CardHeader>
            {/* Signed-out screen renders outside the Shell, so this is the page's top-level
                heading — expose it as a real <h1> (WCAG 2.2 SC 1.3.1), not a styled <div>. */}
            <CardTitle asChild>
              <h1>{t('shell:appName')}</h1>
            </CardTitle>
            <CardDescription>{t('auth:signInPrompt')}</CardDescription>
            {sessionEnded && (
              <p
                role="status"
                className="flex items-start gap-2 rounded-md px-3 py-2 text-sm"
                style={{ background: 'var(--attention-tint)', color: 'var(--attention-ink)' }}
              >
                <Lock aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                <span>
                  <strong className="font-semibold">{t('auth:sessionEndedTitle')}</strong>{' '}
                  {t('auth:sessionEndedAction')}
                </span>
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <Button className="w-full" onClick={handleSignIn}>
              {t('auth:signInWithGoogle')}
            </Button>
            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="space-y-3 border-t pt-4">
              <p className="text-muted-foreground text-sm">
                {emailLinkOutcome === 'email-required'
                  ? t('auth:emailLink.emailRequired')
                  : t('auth:emailLink.prompt')}
              </p>
              {emailLinkOutcome === 'failed' && (
                <p className="text-sm text-destructive" role="alert">
                  {t('auth:emailLink.failed')}
                </p>
              )}
              {emailLinkOutcome === 'sent' ? (
                <div className="space-y-2">
                  <p className="text-success text-sm" role="status">
                    {t('auth:emailLink.sent')}
                  </p>
                  {/* A mistyped address would otherwise be a dead end: the form is gone and only a
                      reload brings it back. This returns to it without leaving the page. */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    onClick={resetEmailLinkRequest}
                  >
                    {t('auth:emailLink.sendAgain')}
                  </Button>
                </div>
              ) : (
                <form
                  className="space-y-2"
                  onSubmit={(event) =>
                    void handleSubmit((values) => run(() => onEmailLinkSubmit(values)))(event)
                  }
                >
                  <label className="field-label" htmlFor="email-link-address">
                    {t('auth:emailLink.emailLabel')}
                  </label>
                  <Input
                    id="email-link-address"
                    autoComplete="email"
                    {...register('email', {
                      required: t('auth:emailLink.required'),
                      pattern: {
                        value: /^\S+@\S+\.\S+$/,
                        message: t('auth:emailLink.invalid'),
                      },
                    })}
                  />
                  {errors.email && (
                    <p className="text-sm text-destructive">{errors.email.message}</p>
                  )}
                  {emailLinkError && <p className="text-sm text-destructive">{emailLinkError}</p>}
                  <Button type="submit" variant="outline" className="w-full" disabled={pending}>
                    {emailLinkOutcome === 'email-required'
                      ? t('auth:emailLink.completeButton')
                      : t('auth:emailLink.sendButton')}
                  </Button>
                </form>
              )}
            </div>
          </CardContent>
        </Card>
        <PrivacyNoticeSummary />
      </div>
    </div>
  );
}
